<div class="relative">
  <loading-overlay [isLoading]="loading()" />

  @if (report() && (processSummary().length > 0 || fabricRows().length > 0)) {
    <!-- Process Summary -->
    <section class="section-block">
      <h3 class="section-title">{{ 'Cost_Analysis_Process_Summary' | translateWF: 'Process Summary' }}</h3>
      <p-table [value]="processSummary()" styleClass="process-summary-table" [stripedRows]="true" sortMode="single">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="label">
              {{ 'Cost_Analysis_Process' | translateWF: 'Process' }}
              <p-sortIcon field="label" />
            </th>
            <th class="num-col" pSortableColumn="totalLbs">
              {{ 'Cost_Analysis_Lbs' | translateWF: 'Lbs' }}
              <p-sortIcon field="totalLbs" />
            </th>
            <th class="num-col" pSortableColumn="realCostPerLbUsd">
              {{ 'Cost_Analysis_Real' | translateWF: 'Real' }} ($/lb)
              <p-sortIcon field="realCostPerLbUsd" />
            </th>
            <th class="num-col" pSortableColumn="standardCostPerLbUsd">
              {{ 'Cost_Analysis_Std' | translateWF: 'Standard' }} ($/lb)
              <p-sortIcon field="standardCostPerLbUsd" />
            </th>
            <th class="num-col" pSortableColumn="variancePerLb">
              {{ 'Cost_Analysis_Variance' | translateWF: 'Variance' }} ($/lb)
              <p-sortIcon field="variancePerLb" />
            </th>
            <th class="num-col" pSortableColumn="variancePct">
              {{ 'Cost_Analysis_Variance_Pct' | translateWF: 'Variance %' }}
              <p-sortIcon field="variancePct" />
            </th>
            <th class="num-col" pSortableColumn="cumulativeCostPerLbUsd">
              {{ 'Cost_Analysis_Cumulative' | translateWF: 'Cumulative' }} ($/lb)
              <p-sortIcon field="cumulativeCostPerLbUsd" />
            </th>
            <th class="num-col" pSortableColumn="pctOfTotal">
              {{ 'Cost_Analysis_Pct_Total' | translateWF: '% of Total' }}
              <p-sortIcon field="pctOfTotal" />
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr [class.row-pending]="row.isPending">
            <td>
              <div class="row-sm-start-center">
                @if (hasSubRows(row)) {
                  <button
                    type="button"
                    class="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--p-text-muted-color)] transition-colors hover:bg-[var(--p-content-hover-background)] hover:text-[var(--p-text-color)]"
                    [attr.aria-expanded]="isExpanded(row)"
                    [attr.aria-label]="
                      isExpanded(row)
                        ? ('Cost_Analysis_Collapse_Work_Areas' | translateWF: 'Collapse work areas')
                        : ('Cost_Analysis_Expand_Work_Areas' | translateWF: 'Expand work areas')
                    "
                    (click)="toggleSubRows(row)"
                  >
                    <i class="pi text-xs" [class.pi-chevron-right]="!isExpanded(row)" [class.pi-chevron-down]="isExpanded(row)"></i>
                  </button>
                } @else {
                  <span class="h-5 w-5 shrink-0"></span>
                }
                <span class="process-badge">{{ row.label }}</span>
              </div>
            </td>
            <td class="num-col">
              @if (!isRawMaterialRow(row) && row.totalLbs > 0) {
                {{ row.totalLbs | number: '1.0-0' }}
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td
              class="num-col"
              [class.variance-good]="row.standardCostPerLbUsd > 0 && row.variancePerLb < -0.0001"
              [class.variance-bad]="row.standardCostPerLbUsd > 0 && row.variancePerLb > 0.0001"
            >
              @if (row.realCostPerLbUsd > 0) {
                ${{ row.realCostPerLbUsd | number: '1.4-4' }}
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td class="num-col">
              @if (row.standardCostPerLbUsd > 0) {
                ${{ row.standardCostPerLbUsd | number: '1.4-4' }}
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td
              class="num-col"
              [class.variance-good]="!isRawMaterialRow(row) && row.standardCostPerLbUsd > 0 && row.variancePerLb < -0.0001"
              [class.variance-bad]="!isRawMaterialRow(row) && row.standardCostPerLbUsd > 0 && row.variancePerLb > 0.0001"
            >
              @if (!isRawMaterialRow(row) && row.standardCostPerLbUsd > 0) {
                @if (row.variancePerLb > 0.0001) {
                  <i class="pi pi-arrow-up variance-arrow"></i>
                } @else if (row.variancePerLb < -0.0001) {
                  <i class="pi pi-arrow-down variance-arrow"></i>
                }
                ${{ row.variancePerLb | number: '1.4-4' }}
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td
              class="num-col"
              [class.variance-good]="!isRawMaterialRow(row) && row.variancePct !== null && row.variancePerLb < -0.0001"
              [class.variance-bad]="!isRawMaterialRow(row) && row.variancePct !== null && row.variancePerLb > 0.0001"
            >
              @if (!isRawMaterialRow(row) && row.variancePct !== null) {
                @if (row.variancePerLb > 0.0001) {
                  <i class="pi pi-arrow-up variance-arrow"></i>
                } @else if (row.variancePerLb < -0.0001) {
                  <i class="pi pi-arrow-down variance-arrow"></i>
                }
                {{ row.variancePct | number: '1.1-1' }}%
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td class="num-col">
              @if (row.cumulativeCostPerLbUsd > 0) {
                <strong>${{ row.cumulativeCostPerLbUsd | number: '1.4-4' }}</strong>
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
            <td class="num-col">
              @if (row.pctOfTotal > 0) {
                {{ row.pctOfTotal | number: '1.1-1' }}%
              } @else {
                <span class="cell-dash">—</span>
              }
            </td>
          </tr>
          @if (isExpanded(row)) {
            @for (sub of row.subRows; track sub.label) {
              <tr class="bg-[var(--p-content-hover-background)] text-[var(--p-text-muted-color)]">
                <td>
                  <span class="ml-7 text-xs font-medium tracking-wide">{{ subRowLabelKey(sub) | translateWF: subRowLabelFallback(sub) }}</span>
                </td>
                <td class="num-col">
                  @if (sub.totalLbs > 0) {
                    {{ sub.totalLbs | number: '1.0-0' }}
                  } @else {
                    <span class="cell-dash">—</span>
                  }
                </td>
                <td class="num-col">
                  @if (sub.realCostPerLbUsd > 0) {
                    ${{ sub.realCostPerLbUsd | number: '1.4-4' }}
                  } @else {
                    <span class="cell-dash">—</span>
                  }
                </td>
                <td class="num-col">
                  @if (sub.standardCostPerLbUsd > 0) {
                    ${{ sub.standardCostPerLbUsd | number: '1.4-4' }}
                  } @else {
                    <span class="cell-dash">—</span>
                  }
                </td>
                <td
                  class="num-col"
                  [class.variance-good]="sub.standardCostPerLbUsd > 0 && sub.variancePerLb < -0.0001"
                  [class.variance-bad]="sub.standardCostPerLbUsd > 0 && sub.variancePerLb > 0.0001"
                >
                  @if (sub.standardCostPerLbUsd > 0) {
                    @if (sub.variancePerLb > 0.0001) {
                      <i class="pi pi-arrow-up variance-arrow"></i>
                    } @else if (sub.variancePerLb < -0.0001) {
                      <i class="pi pi-arrow-down variance-arrow"></i>
                    }
                    ${{ sub.variancePerLb | number: '1.4-4' }}
                  } @else {
                    <span class="cell-dash">—</span>
                  }
                </td>
                <td
                  class="num-col"
                  [class.variance-good]="sub.variancePct !== null && sub.variancePerLb < -0.0001"
                  [class.variance-bad]="sub.variancePct !== null && sub.variancePerLb > 0.0001"
                >
                  @if (sub.variancePct !== null) {
                    @if (sub.variancePerLb > 0.0001) {
                      <i class="pi pi-arrow-up variance-arrow"></i>
                    } @else if (sub.variancePerLb < -0.0001) {
                      <i class="pi pi-arrow-down variance-arrow"></i>
                    }
                    {{ sub.variancePct | number: '1.1-1' }}%
                  } @else {
                    <span class="cell-dash">—</span>
                  }
                </td>
                <td class="num-col"><span class="cell-dash">—</span></td>
                <td class="num-col"><span class="cell-dash">—</span></td>
              </tr>
            }
          }
        </ng-template>
      </p-table>
    </section>

    <!-- Production (net Finished-Goods movement for the period) -->
    <section class="production-block mt-6">
      <h3 class="section-title">{{ 'Cost_Analysis_Production' | translateWF: 'Production' }}</h3>
      <div class="production-value">
        <span class="tabular-nums">{{ productionLbs() | number: '1.0-0' }}</span>
        <span class="production-unit">lbs</span>
      </div>
    </section>

    <!-- Fabric × Process Matrix -->
    <section class="section-block mt-6">
      <h3 class="section-title">{{ 'Cost_Analysis_Fabric_Process_Matrix' | translateWF: 'Fabric × Process Matrix ($/lb)' }}</h3>
      <p-table
        [value]="fabricRows()"
        styleClass="matrix-table p-datatable-sm"
        [stripedRows]="true"
        [scrollable]="true"
        scrollHeight="flex"
        sortMode="single"
        [sortField]="'totalCostPerLbUsd'"
        [sortOrder]="-1"
      >
        <ng-template pTemplate="header">
          <tr>
            <th class="fabric-col" pSortableColumn="fabricCode">
              {{ 'Cost_Analysis_Fabric' | translateWF: 'Fabric' }}
              <p-sortIcon field="fabricCode" />
            </th>
            <th class="num-col total-col bold-col" pSortableColumn="totalCostPerLbUsd">
              {{ 'Cost_Analysis_Total' | translateWF: 'Total' }} ($/lb)
              <p-sortIcon field="totalCostPerLbUsd" />
            </th>
            @for (process of processes; track process) {
              <th class="num-col proc-col" [pSortableColumn]="'byProcess.' + process + '.costPerLbUsd'">
                {{ processLabels[process] }}
                <p-sortIcon [field]="'byProcess.' + process + '.costPerLbUsd'" />
              </th>
            }
            <th class="num-col bold-col" pSortableColumn="totalLbs">
              {{ 'Cost_Analysis_Lbs' | translateWF: 'Lbs' }}
              <p-sortIcon field="totalLbs" />
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-fabric>
          <tr>
            <td class="fabric-col">
              @if (fabric.fabricDescription && fabric.fabricDescription !== fabric.fabricCode) {
                <span class="fabric-name" [pTooltip]="fabric.fabricDescription" tooltipPosition="top">{{ fabric.fabricDescription }}</span>
                <span class="fabric-code-secondary font-mono">{{ fabric.fabricCode }}</span>
              } @else {
                <span class="fabric-name">{{ fabric.fabricCode }}</span>
              }
              <app-cost-mode-chip [mode]="fabric.costMode" />
            </td>
            <td class="num-col total-col bold-col">
              <strong>${{ fabric.totalCostPerLbUsd | number: '1.4-4' }}</strong>
            </td>
            @for (process of processes; track process) {
              @let bucket = fabric.byProcess[process];
              <td class="num-col proc-col" [class.cell-zero]="bucket.totalCostUsd === 0">
                @if (bucket.totalCostUsd > 0) {
                  <button
                    type="button"
                    class="proc-cell-btn inline-flex cursor-pointer items-center gap-1"
                    [pTooltip]="'Cost_Analysis_View_Process_Detail' | translateWF: 'View process detail'"
                    tooltipPosition="top"
                    (click)="onProcessClick(fabric, process)"
                  >
                    <span class="tabular-nums">${{ bucket.costPerLbUsd | number: '1.4-4' }}</span>
                    <i class="pi pi-arrow-circle-right text-[0.65rem] text-[var(--p-primary-500)]"></i>
                  </button>
                } @else {
                  <span class="cell-dash">—</span>
                }
              </td>
            }
            <td class="num-col bold-col">
              <strong>{{ fabric.totalLbs | number: '1.0-0' }}</strong>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="footer">
          <tr class="totals-row">
            <td class="fabric-col">
              <strong>{{ 'Cost_Analysis_Grand_Total' | translateWF: 'Grand Total' }}</strong>
            </td>
            <td class="num-col total-col bold-col">
              <strong>${{ matrixTotals().sumOfProcessCostPerLb | number: '1.4-4' }}</strong>
            </td>
            @for (process of processes; track process) {
              <td class="num-col proc-col">
                @if (matrixTotals().processCostPerLb[process] > 0) {
                  <strong>${{ matrixTotals().processCostPerLb[process] | number: '1.4-4' }}</strong>
                } @else {
                  <span class="cell-dash">—</span>
                }
              </td>
            }
            <td class="num-col bold-col">
              <strong>{{ matrixTotals().totalLbs | number: '1.0-0' }}</strong>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  } @else if (!loading()) {
    <div class="empty-state">
      {{ 'Cost_Analysis_No_Data' | translateWF: 'No data for the selected period.' }}
    </div>
  }
</div>
